<template>
  <div>
    <div
      v-if="!isProduction"
      class="dev-line"
    >
      <q-tooltip>Not production environment</q-tooltip>
    </div>
    <q-linear-progress
      v-if="mutationLoading || queryLoading"
      class="global-progress"
      color="amber-10"
      indeterminate
    />
    <router-view />
    <NetworkStatusDialog />
    <BuildVersion />
  </div>
</template>
<script setup>
import { provide, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useFavicon } from '@vueuse/core';
import { DefaultApolloClient, useGlobalMutationLoading, useGlobalQueryLoading } from '@vue/apollo-composable';
import { apolloClient } from '@boot/vue-apollo';
import { checkNewAppRefresh } from '@utils/checkNewAppRefresh';
import { checkUserHasTemporaryPassword } from '@utils/checkUserHasTemporaryPassword';
import { windowUnloadProtection } from '@router/guards/unsavedProtectionGuard';
import { useNotifications } from '@utils/notifications';
import { getTodayTasks, notifyUserAboutDaily } from '@utils/getTodayTasks';
import { useChatNotifications } from '@utils/chat';
import NetworkStatusDialog from '@components/misc/NetworkStatusDialog';
import BuildVersion from '@components/misc/BuildVersion';

provide(DefaultApolloClient, apolloClient);

const notifications = useNotifications();
provide('notifications', notifications);

const mutationLoading = useGlobalMutationLoading();
const queryLoading = useGlobalQueryLoading();
const isProduction = process.env.ENV === 'production';

if (!isProduction) {
  useFavicon('icons/favicon-dev.png');
}

onMounted(() => {
  useChatNotifications();

  // Check user has temporary password
  checkUserHasTemporaryPassword();

  // Check new version every 10 min
  checkNewAppRefresh(1000 * 60 * 10);

  // Update today's tasks every 10 min (sidebar badge)
  getTodayTasks(1000 * 60 * 10);

  // Notify user every 12 hours about daily
  notifyUserAboutDaily(1000 * 60 * 60 * 12);

  // Protect window unload (close) if it has protected form
  windowUnloadProtection();
});

const $store = useStore();
const { locale } = useI18n();
locale.value = $store.getters['app/locale'];

</script>
<style scoped>
.global-progress {
  position: fixed;
  z-index: 3000;
  top: 0;
  left: 0;
  width: 100%;
}

.dev-line {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 10000;
  height: 10px;
  background-image: linear-gradient(
    45deg,
    #000000 0%,
    #000000 10%,
    #ffe363 10%,
    #ffe363 20%,
    #000000 20%,
    #000000 30%,
    #ffe363 30%,
    #ffe363 40%,
    #000000 40%,
    #000000 50%,
    #ffe363 50%,
    #ffe363 60%,
    #000000 60%,
    #000000 70%,
    #ffe363 70%,
    #ffe363 80%,
    #000000 80%,
    #000000 90%,
    #ffe363 90%,
    #ffe363 100%
  );
}
</style>
